import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CabinCrewLayout from '../layouts/CabinCrewLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, ArrowRight, BookOpen, Clock, CheckCircle,
  ChevronRight, GraduationCap, Zap
} from 'lucide-react';

// Tüm İngilizce Zamanlar
const TENSES = {
  present: {
    title: "Present Tenses (Şimdiki Zamanlar)",
    color: "blue",
    tenses: [
      {
        id: "present_simple",
        name: "Present Simple",
        turkish: "Geniş Zaman",
        level: "A1",
        usage: "Alışkanlıklar, genel gerçekler, rutinler",
        structure: "I/You/We/They + V1 | He/She/It + V1+s",
        example: "I work every day.",
        exampleTr: "Her gün çalışırım.",
        keywords: ["always", "usually", "every day", "sometimes", "never"]
      },
      {
        id: "present_continuous",
        name: "Present Continuous",
        turkish: "Şimdiki Zaman",
        level: "A1",
        usage: "Şu an olan eylemler, geçici durumlar",
        structure: "am/is/are + V-ing",
        example: "I am working now.",
        exampleTr: "Şu an çalışıyorum.",
        keywords: ["now", "right now", "at the moment", "currently"]
      },
      {
        id: "present_perfect",
        name: "Present Perfect",
        turkish: "Yakın Geçmiş / Deneyim",
        level: "A2",
        usage: "Deneyimler, yakın geçmiş, devam eden durumlar",
        structure: "have/has + V3 (past participle)",
        example: "I have visited Paris.",
        exampleTr: "Paris'i ziyaret ettim.",
        keywords: ["ever", "never", "already", "yet", "just", "since", "for"]
      },
      {
        id: "present_perfect_continuous",
        name: "Present Perfect Continuous",
        turkish: "Süregelen Yakın Geçmiş",
        level: "B1",
        usage: "Geçmişte başlayıp hala devam eden eylemler",
        structure: "have/has + been + V-ing",
        example: "I have been working for 3 hours.",
        exampleTr: "3 saattir çalışıyorum.",
        keywords: ["for", "since", "how long", "all day"]
      }
    ]
  },
  past: {
    title: "Past Tenses (Geçmiş Zamanlar)",
    color: "amber",
    tenses: [
      {
        id: "past_simple",
        name: "Past Simple",
        turkish: "Geçmiş Zaman",
        level: "A2",
        usage: "Geçmişte biten eylemler",
        structure: "V2 (past form) | did + V1",
        example: "I worked yesterday.",
        exampleTr: "Dün çalıştım.",
        keywords: ["yesterday", "last week", "ago", "in 2020"]
      },
      {
        id: "past_continuous",
        name: "Past Continuous",
        turkish: "Geçmişte Süren",
        level: "B1",
        usage: "Geçmişte devam eden eylemler",
        structure: "was/were + V-ing",
        example: "I was working at 5 PM.",
        exampleTr: "Saat 5'te çalışıyordum.",
        keywords: ["while", "when", "as", "at that time"]
      },
      {
        id: "past_perfect",
        name: "Past Perfect",
        turkish: "Miş'li Geçmiş",
        level: "B1",
        usage: "Geçmişteki bir olaydan önce olan",
        structure: "had + V3",
        example: "I had finished before she came.",
        exampleTr: "O gelmeden bitirmiştim.",
        keywords: ["before", "after", "by the time", "already"]
      },
      {
        id: "past_perfect_continuous",
        name: "Past Perfect Continuous",
        turkish: "Süregelen Miş'li Geçmiş",
        level: "B2",
        usage: "Geçmişte bir noktaya kadar süren eylemler",
        structure: "had + been + V-ing",
        example: "I had been waiting for 2 hours.",
        exampleTr: "2 saattir bekliyordum.",
        keywords: ["for", "since", "how long", "before"]
      }
    ]
  },
  future: {
    title: "Future Tenses (Gelecek Zamanlar)",
    color: "green",
    tenses: [
      {
        id: "future_simple",
        name: "Future Simple (will)",
        turkish: "Gelecek Zaman",
        level: "A2",
        usage: "Anlık kararlar, tahminler, söz verme",
        structure: "will + V1",
        example: "I will help you.",
        exampleTr: "Sana yardım edeceğim.",
        keywords: ["tomorrow", "next week", "I think", "probably"]
      },
      {
        id: "future_going_to",
        name: "Going to",
        turkish: "Planlı Gelecek",
        level: "A2",
        usage: "Önceden planlanmış, kanıta dayalı tahminler",
        structure: "am/is/are + going to + V1",
        example: "I am going to travel.",
        exampleTr: "Seyahat edeceğim.",
        keywords: ["plan", "intend", "look at"]
      },
      {
        id: "future_continuous",
        name: "Future Continuous",
        turkish: "Gelecekte Süren",
        level: "B1",
        usage: "Gelecekte devam edecek eylemler",
        structure: "will + be + V-ing",
        example: "I will be working at 9 AM.",
        exampleTr: "Saat 9'da çalışıyor olacağım.",
        keywords: ["this time tomorrow", "at 5 PM"]
      },
      {
        id: "future_perfect",
        name: "Future Perfect",
        turkish: "Gelecekte Tamamlanmış",
        level: "B2",
        usage: "Gelecekte bir noktadan önce bitmiş olacak",
        structure: "will + have + V3",
        example: "I will have finished by 5 PM.",
        exampleTr: "Saat 5'e kadar bitirmiş olacağım.",
        keywords: ["by", "by the time", "before"]
      }
    ]
  }
};

// Diğer Gramer Konuları
const OTHER_GRAMMAR = [
  {
    id: "prepositions_basic",
    name: "Prepositions (in, on, at)",
    turkish: "Edatlar",
    level: "A1",
    description: "Yer ve zaman edatları"
  },
  {
    id: "basic_questions",
    name: "Wh- Questions",
    turkish: "Soru Kelimeleri",
    level: "A1",
    description: "What, Where, When, Who, Why, How"
  },
  {
    id: "plurals_articles",
    name: "Articles & Plurals",
    turkish: "Artikeller ve Çoğullar",
    level: "A1",
    description: "a/an/the ve çoğul yapımı"
  },
  {
    id: "can_cant",
    name: "Can / Can't",
    turkish: "Yetenek ve İzin",
    level: "A1",
    description: "Yapabilmek, izin almak"
  },
  {
    id: "modal_verbs",
    name: "Modal Verbs",
    turkish: "Yardımcı Fiiller",
    level: "A2",
    description: "should, must, may, might, could"
  },
  {
    id: "comparatives",
    name: "Comparatives & Superlatives",
    turkish: "Karşılaştırma",
    level: "A2",
    description: "bigger, the biggest, more beautiful"
  },
  {
    id: "adverbs_frequency",
    name: "Adverbs of Frequency",
    turkish: "Sıklık Zarfları",
    level: "A2",
    description: "always, usually, sometimes, never"
  }
];

const TensesOverview = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('present');
  
  const renderTenseCard = (tense, groupColor) => {
    const colorClasses = {
      blue: {
        border: 'border-blue-200 hover:border-blue-400',
        badge: 'bg-blue-100 text-blue-700',
        icon: 'text-blue-600',
        button: 'from-blue-600 to-blue-700'
      },
      amber: {
        border: 'border-amber-200 hover:border-amber-400',
        badge: 'bg-amber-100 text-amber-700',
        icon: 'text-amber-600',
        button: 'from-amber-600 to-amber-700'
      },
      green: {
        border: 'border-green-200 hover:border-green-400',
        badge: 'bg-green-100 text-green-700',
        icon: 'text-green-600',
        button: 'from-green-600 to-green-700'
      }
    };
    
    const colors = colorClasses[groupColor];
    
    return (
      <Card 
        key={tense.id}
        className={`${colors.border} border-2 hover:shadow-lg transition-all cursor-pointer`}
        onClick={() => navigate(`/grammar-lesson/${tense.level}/${tense.id}`)}
      >
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between mb-2">
            <Badge className={colors.badge}>{tense.level}</Badge>
            <Clock className={`w-5 h-5 ${colors.icon}`} />
          </div>
          <CardTitle className="text-lg">{tense.name}</CardTitle>
          <p className="text-sm font-medium text-gray-700">{tense.turkish}</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-gray-600">{tense.usage}</p>
          
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Yapı:</p>
            <p className="text-sm font-mono text-gray-800">{tense.structure}</p>
          </div>
          
          <div className="bg-gradient-to-r from-gray-50 to-white rounded-lg p-3 border">
            <p className="text-sm font-medium text-gray-800">&quot;{tense.example}&quot;</p>
            <p className="text-xs text-gray-600 mt-1">{tense.exampleTr}</p>
          </div>
          
          <div className="flex flex-wrap gap-1">
            {tense.keywords.slice(0, 4).map((kw, idx) => (
              <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                {kw}
              </span>
            ))}
          </div>
          
          <Button className={`w-full bg-gradient-to-r ${colors.button} mt-2`}>
            Detaylı Öğren
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </CardContent>
      </Card>
    );
  };
  
  return (
    <CabinCrewLayout>
      <div className="min-h-screen py-6 sm:py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Header */}
          <div className="mb-6">
            <Button
              variant="outline"
              onClick={() => navigate('/exercises')}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Geri Dön
            </Button>
            
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">İngilizce Zamanlar</h1>
                <p className="text-gray-600">Tenses - Tüm zamanları öğren</p>
              </div>
            </div>
          </div>
          
          {/* Info Box */}
          <Card className="mb-6 bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start gap-4">
                <GraduationCap className="w-8 h-8 text-indigo-600 shrink-0" />
                <div>
                  <h3 className="font-bold text-indigo-800 mb-1">Nasıl Öğreneceksin?</h3>
                  <p className="text-sm text-indigo-700">
                    Her zaman kartına tıklayarak detaylı ders sayfasına git. 
                    Kullanım kurallarını, yapısını ve örnekleri öğren. 
                    Sonra Gramer Pratik sekmesinden egzersiz yap!
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Tenses Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="present" className="text-xs sm:text-sm">
                🔵 Present (Şimdi)
              </TabsTrigger>
              <TabsTrigger value="past" className="text-xs sm:text-sm">
                🟠 Past (Geçmiş)
              </TabsTrigger>
              <TabsTrigger value="future" className="text-xs sm:text-sm">
                🟢 Future (Gelecek)
              </TabsTrigger>
            </TabsList>
            
            {/* Present Tenses */}
            <TabsContent value="present">
              <div className="mb-4">
                <h2 className="text-xl font-bold text-blue-700 flex items-center gap-2">
                  <span className="text-2xl">🔵</span>
                  {TENSES.present.title}
                </h2>
                <p className="text-gray-600 text-sm mt-1">
                  Şu an veya genel olarak olan şeylerden bahsetmek için kullanılır.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {TENSES.present.tenses.map(tense => renderTenseCard(tense, 'blue'))}
              </div>
            </TabsContent>
            
            {/* Past Tenses */}
            <TabsContent value="past">
              <div className="mb-4">
                <h2 className="text-xl font-bold text-amber-700 flex items-center gap-2">
                  <span className="text-2xl">🟠</span>
                  {TENSES.past.title}
                </h2>
                <p className="text-gray-600 text-sm mt-1">
                  Geçmişte olan şeylerden bahsetmek için kullanılır.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {TENSES.past.tenses.map(tense => renderTenseCard(tense, 'amber'))}
              </div>
            </TabsContent>
            
            {/* Future Tenses */}
            <TabsContent value="future">
              <div className="mb-4">
                <h2 className="text-xl font-bold text-green-700 flex items-center gap-2">
                  <span className="text-2xl">🟢</span>
                  {TENSES.future.title}
                </h2>
                <p className="text-gray-600 text-sm mt-1">
                  Gelecekte olacak şeylerden bahsetmek için kullanılır.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {TENSES.future.tenses.map(tense => renderTenseCard(tense, 'green'))}
              </div>
            </TabsContent>
          </Tabs>
          
          {/* Other Grammar Topics */}
          <div className="mt-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-purple-600" />
              Diğer Gramer Konuları
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {OTHER_GRAMMAR.map(topic => (
                <Card 
                  key={topic.id}
                  className="border-purple-200 hover:border-purple-400 border-2 hover:shadow-lg transition-all cursor-pointer"
                  onClick={() => navigate(`/grammar-lesson/${topic.level}/${topic.id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <Badge className="bg-purple-100 text-purple-700">{topic.level}</Badge>
                    </div>
                    <h3 className="font-bold text-gray-800">{topic.name}</h3>
                    <p className="text-sm text-gray-600 mb-1">{topic.turkish}</p>
                    <p className="text-xs text-gray-500">{topic.description}</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full mt-3 border-purple-300 text-purple-700"
                    >
                      Öğren
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          
          {/* Quick Tip */}
          <Card className="mt-8 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start gap-4">
                <Zap className="w-8 h-8 text-amber-600 shrink-0" />
                <div>
                  <h3 className="font-bold text-amber-800 mb-1">İpucu</h3>
                  <p className="text-sm text-amber-700">
                    Zamanları öğrenirken anahtar kelimelere dikkat et! Her zamanın kendine özgü 
                    zaman zarfları vardır. Örneğin yesterday görünce Past Simple, now görünce 
                    Present Continuous kullan.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </CabinCrewLayout>
  );
};

export default TensesOverview;
